import React, { useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TurndownService from 'turndown';
import { marked } from 'marked';
import ReactMarkdown from 'react-markdown';
import './MarkdownEditor.css';

const turndownService = new TurndownService();

interface MarkdownEditorProps {
  onMarkdownChange: (markdown: string) => void;
  value?: string;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ onMarkdownChange, value }) => {
  const [markdown, setMarkdown] = useState(value || '');
  // Convert markdown to HTML for initial content
  const initialHTML = value ? marked.parse(value) : '';
  const editor: Editor | null = useEditor({
    extensions: [StarterKit, Link],
    content: initialHTML,
    editorProps: {
      handleKeyDown(view, event) {
        if (event.key === 'Tab') {
          event.preventDefault();
          view.dispatch(view.state.tr.insertText('  ')); // indent with 2 spaces
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdownString = turndownService.turndown(html);
      setMarkdown(markdownString);
      onMarkdownChange(markdownString);
    },
  });

  if (!editor) return null;

  return (
    <div className="markdown-editor">
      <div className="markdown-editor__toolbar">
        <button 
          type="button"
          className="markdown-editor__button"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Bold
        </button>
        <button 
          type="button"
          className="markdown-editor__button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Italic
        </button>
        <button 
          type="button"
          className="markdown-editor__button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </button>
        <button 
          type="button"
          className="markdown-editor__button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </button>
      </div>

      <EditorContent editor={editor} className="markdown-editor__content" />
    </div>
  );
};

export default MarkdownEditor;
